// Students' homework submissions on IZTeach (OnlineExam_Homework), joined to the period's QLT roster.
// Import-free apart from types and import-free modules, so node --test can load it.

import type { IztHomework, IztHomeworkResource, QltAvatar, QltSlotAttendance } from '../../api/types';
import { fnv, SUBMISSION_DOCUMENT_PREFIX, type BoardDocument } from '../filmstrip/documents.ts';
import { uploadViewer, type UploadViewer } from '../upload/validation.ts';

// shown instead of querying when VITE_IZT_STUDENT_EMAIL_DOMAIN is unset
export const STUDENT_EMAIL_DOMAIN_UNSET = 'Bản build chưa cấu hình tên miền email học sinh (VITE_IZT_STUDENT_EMAIL_DOMAIN), nên chưa tìm được bài nộp.';

const VN_OFFSET_MS = 7 * 60 * 60 * 1000;
const UNTITLED_TASK = 'Bài tập về nhà';

export interface RosterStudent {
  id: string;
  /** Lower-cased QLT student code, the local part of the IZ email. */
  code: string;
  name: string;
  avatar?: QltAvatar | null;
}

export interface SubmissionFile {
  id: string;
  name: string;
  mime: string;
  size: number;
  url: string;
  viewer: UploadViewer;
}

export interface StudentSubmission {
  id: string;
  code: string;
  studentName: string;
  taskId: string;
  taskTitle: string;
  courseId: string;
  submittedAt: string;
  approved: boolean;
  mark: number | null;
  html: string;
  link: string;
  files: SubmissionFile[];
}

export interface SubmissionTask {
  id: string;
  title: string;
  courseId: string;
  latestAt: string;
  schoolYear: string;
  /** The newest submission per student code. */
  byCode: Record<string, StudentSubmission>;
  count: number;
}

export interface RosterEntry {
  student: RosterStudent;
  submission: StudentSubmission | null;
}

export type SubmissionItem =
  | { kind: 'answer' }
  | { kind: 'file'; file: SubmissionFile }
  | { kind: 'link'; url: string };

export function studentCodeKey(code: string | null | undefined): string {
  return String(code || '').trim().toLowerCase();
}

/** The period's roster with a usable code, in the attendance order. */
export function rosterOf(rows: QltSlotAttendance[]): RosterStudent[] {
  const out: RosterStudent[] = [];
  const seen: Record<string, boolean> = {};
  rows.forEach((row) => {
    const code = studentCodeKey(row.student && row.student.code);
    if (!code || seen[code]) return;
    seen[code] = true;
    out.push({ id: String(row.student._id || row.student.id || code), code, name: (row.student.name || '').trim() || code, avatar: row.student.avatar });
  });
  return out;
}

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** One anchored regex for the whole class, so the roster costs a single request. '' for no codes. */
export function emailPattern(codes: string[], domain: string): string {
  const keys: string[] = [];
  codes.forEach((code) => {
    const key = studentCodeKey(code);
    if (key && keys.indexOf(key) < 0) keys.push(key);
  });
  if (keys.length === 0) return '';
  return `^(${keys.map(escapeRegex).join('|')})@${escapeRegex(domain)}$`;
}

export function codeOfEmail(email: string | null | undefined, domain: string): string {
  const value = studentCodeKey(email);
  const suffix = `@${domain.trim().toLowerCase()}`;
  return value.length > suffix.length && value.slice(-suffix.length) === suffix ? value.slice(0, -suffix.length) : '';
}

const IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/** Student phones send video/quicktime too; the player says so if the WebView cannot play one. */
export function fileViewer(mime: string, name: string): UploadViewer {
  const type = mime.toLowerCase();
  if (IMAGE_MIMES.indexOf(type) >= 0) return 'image';
  if (type.indexOf('video/') === 0) return 'video';
  if (type.indexOf('audio/') === 0) return 'audio';
  if (type === 'application/pdf') return 'pdf';
  return uploadViewer({ name, mime: type });
}

export type ResolveFileUrl = (link: string, bucket: string | null) => string;

function toFile(resource: IztHomeworkResource, index: number, resolveUrl: ResolveFileUrl): SubmissionFile | null {
  const link = typeof resource.link === 'string' ? resource.link.trim() : '';
  if (!link) return null;
  const origin = resource.origin || {};
  const mime = String(origin.mimeType || '');
  const name = String(resource.title || '').trim() || link.split('/').pop() || `Tệp ${index + 1}`;
  return { id: String(resource.id || `${index}`), name, mime, size: Number(origin.size || resource.size || 0), url: resolveUrl(link, origin.bucket || null), viewer: fileViewer(mime, name) };
}

/** Null for a row the board cannot place: no id, no school email, or no task. */
export function toSubmission(row: IztHomework, domain: string, resolveUrl: ResolveFileUrl): StudentSubmission | null {
  const id = String(row.id || row._id || '');
  const code = codeOfEmail(row.email, domain);
  const unit = row.unit && typeof row.unit === 'object' ? row.unit : null;
  const unitId = unit ? String(unit.id || unit._id || '') : String(row.unit || '');
  const courseId = String(row.course || '');
  const taskTitle = String((unit && unit.title) || row.title || '').trim() || UNTITLED_TASK;
  if (!id || !code || (!unitId && !courseId)) return null;
  const link = typeof row.link === 'string' ? row.link.trim() : '';
  const files: SubmissionFile[] = [];
  (Array.isArray(row.resources) ? row.resources : []).forEach((resource, index) => {
    const file = resource ? toFile(resource, index, resolveUrl) : null;
    if (file) files.push(file);
  });
  return {
    id,
    code,
    studentName: String(row.name || '').trim() || code,
    taskId: unitId || `${courseId}|${taskTitle}`,
    taskTitle,
    courseId,
    submittedAt: String(row.submitTime || row.createdAt || ''),
    approved: row.state === 'approved',
    mark: typeof row.mark === 'number' ? row.mark : null,
    html: typeof row.html === 'string' ? row.html : '',
    link: /^https?:\/\//i.test(link) ? link : '',
    files,
  };
}

function timeOf(iso: string): number {
  const time = Date.parse(iso);
  return isNaN(time) ? 0 : time;
}

/** "2025-2026"; a VN school year turns over in August. */
export function schoolYearOf(iso: string): string {
  const time = Date.parse(iso);
  if (isNaN(time)) return '';
  const vn = new Date(time + VN_OFFSET_MS);
  const year = vn.getUTCFullYear();
  return vn.getUTCMonth() >= 7 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
}

/** Tasks this roster submitted to, newest first; a resubmission replaces the student's earlier one. */
export function groupTasks(submissions: StudentSubmission[], roster: RosterStudent[]): SubmissionTask[] {
  const inRoster: Record<string, boolean> = {};
  roster.forEach((student) => { inRoster[student.code] = true; });
  const byId: Record<string, SubmissionTask> = {};
  const tasks: SubmissionTask[] = [];
  submissions.forEach((submission) => {
    if (!inRoster[submission.code]) return;
    let task = byId[submission.taskId];
    if (!task) {
      task = { id: submission.taskId, title: submission.taskTitle, courseId: submission.courseId, latestAt: '', schoolYear: '', byCode: {}, count: 0 };
      byId[submission.taskId] = task;
      tasks.push(task);
    }
    const previous = task.byCode[submission.code];
    if (!previous) task.count += 1;
    if (!previous || timeOf(submission.submittedAt) > timeOf(previous.submittedAt)) task.byCode[submission.code] = submission;
    if (timeOf(submission.submittedAt) > timeOf(task.latestAt)) task.latestAt = submission.submittedAt;
  });
  tasks.forEach((task) => { task.schoolYear = schoolYearOf(task.latestAt); });
  return tasks.sort((a, b) => timeOf(b.latestAt) - timeOf(a.latestAt));
}

export function rosterEntries(task: SubmissionTask, roster: RosterStudent[]): RosterEntry[] {
  return roster.map((student) => ({ student, submission: task.byCode[student.code] || null }));
}

/** Chi tiết order: who submitted in roster order (the navigator's), then who did not. */
export function classEntries(task: SubmissionTask, roster: RosterStudent[]): RosterEntry[] {
  const entries = rosterEntries(task, roster);
  return entries.filter((entry) => entry.submission !== null).concat(entries.filter((entry) => entry.submission === null));
}

/** The order next/prev walks: the roster order, students who submitted only. */
export function submittedInOrder(task: SubmissionTask, roster: RosterStudent[]): StudentSubmission[] {
  const out: StudentSubmission[] = [];
  roster.forEach((student) => {
    const submission = task.byCode[student.code];
    if (submission) out.push(submission);
  });
  return out;
}

/** A task keyed by its IZ unit opens that unit as its đề; a row without one only has course and title. */
export function taskHasUnit(task: Pick<SubmissionTask, 'id'>): boolean {
  return task.id.length > 0 && task.id.indexOf('|') < 0;
}

/** The painted stand-in tab for a task with no đề on IZTeach; in memory only, like the submissions. */
export function taskCoverDocument(task: Pick<SubmissionTask, 'id' | 'title'>, courseTitle: string): BoardDocument {
  return {
    id: SUBMISSION_DOCUMENT_PREFIX + 'task-' + fnv(task.id).toString(36),
    title: task.title,
    kind: 'slide',
    pageCount: 1,
    text: { eyebrow: 'BÀI TẬP VỀ NHÀ', title: task.title, meta: courseTitle, html: '', empty: 'Bài tập này chưa có đề trên IZTeach. Thầy/cô chọn học sinh ở mục Chi tiết của bảng Kết quả để mở bài nộp.' },
  };
}

export function hasAnswerText(html: string): boolean {
  return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
}

/** Links a student pasted into a written answer, in order and without repeats. */
export function answerLinks(html: string): string[] {
  const found = String(html || '').replace(/&amp;/g, '&').match(/https?:\/\/[^\s"'<>]+/g) || [];
  const out: string[] = [];
  found.forEach((url) => {
    const clean = url.replace(/[).,;:!?]+$/, '');
    if (out.indexOf(clean) < 0) out.push(clean);
  });
  return out;
}

/** What the navigator steps through for one submission: the written answer, each file, each link. */
export function submissionItems(submission: StudentSubmission): SubmissionItem[] {
  const items: SubmissionItem[] = [];
  if (hasAnswerText(submission.html)) items.push({ kind: 'answer' });
  submission.files.forEach((file) => items.push({ kind: 'file', file }));
  const links = (submission.link ? [submission.link] : []).concat(answerLinks(submission.html));
  links.forEach((url, i) => { if (links.indexOf(url) === i) items.push({ kind: 'link', url }); });
  if (items.length === 0) items.push({ kind: 'answer' });
  return items;
}

const VIEWER_NOUN: Record<UploadViewer, string> = { image: 'Ảnh', pdf: 'PDF', video: 'Video', audio: 'Âm thanh', file: 'Tệp' };

function itemNoun(item: SubmissionItem): string {
  if (item.kind === 'answer') return 'Bài làm';
  if (item.kind === 'link') return 'Liên kết';
  return VIEWER_NOUN[item.file.viewer];
}

/** "Ảnh 2" when a submission has several of a kind, else the bare noun. */
export function itemLabel(items: SubmissionItem[], index: number): string {
  const noun = itemNoun(items[index]);
  const same = items.filter((item) => itemNoun(item) === noun);
  if (same.length < 2) return noun;
  return `${noun} ${same.indexOf(items[index]) + 1}`;
}

/** "Ảnh 3/20" when every item is one kind, else "Phần 3/22": the strip hides most chips. */
export function itemPosition(items: SubmissionItem[], index: number): string {
  const noun = itemNoun(items[index]);
  const uniform = items.every((item) => itemNoun(item) === noun);
  return `${uniform ? noun : 'Phần'} ${index + 1}/${items.length}`;
}

function pad(value: number): string {
  return ('0' + value).slice(-2);
}

/** "HH:mm dd/MM/yyyy" in VN time; old school years make the year worth showing. */
export function vnDateTime(iso: string): string {
  const time = Date.parse(iso);
  if (isNaN(time)) return '';
  const vn = new Date(time + VN_OFFSET_MS);
  return `${pad(vn.getUTCHours())}:${pad(vn.getUTCMinutes())} ${pad(vn.getUTCDate())}/${pad(vn.getUTCMonth() + 1)}/${vn.getUTCFullYear()}`;
}

export function vnDate(iso: string): string {
  return vnDateTime(iso).slice(6);
}

export function statusLabel(submission: StudentSubmission | null): string {
  if (!submission) return 'Chưa nộp';
  if (!submission.approved) return 'Đã nộp';
  return submission.mark === null ? 'Đã chấm' : `Đã chấm · ${String(submission.mark).replace('.', ',')}`;
}

/** The navigator line under the student's name. */
export function navigatorMeta(viewer: { index: number; order: StudentSubmission[]; taskTitle: string }): string {
  const current = viewer.order[viewer.index];
  return [`${viewer.index + 1}/${viewer.order.length}`, viewer.taskTitle, statusLabel(current), current.submittedAt ? vnDateTime(current.submittedAt) : ''].filter(Boolean).join(' • ');
}

function attachmentSummary(items: SubmissionItem[]): string {
  const counts: string[] = [];
  const nouns: string[] = [];
  items.forEach((item) => {
    if (item.kind === 'answer') return;
    const noun = itemNoun(item).toLowerCase();
    const at = nouns.indexOf(noun);
    if (at < 0) { nouns.push(noun); counts.push('1'); } else counts[at] = String(Number(counts[at]) + 1);
  });
  if (nouns.length === 0) return 'Bài nộp này chưa có nội dung.';
  const total = counts.reduce((sum, count) => sum + Number(count), 0);
  const hint = total > 1 ? 'Thầy/cô chọn từng phần trên thanh bài nộp phía trên bảng.' : 'Nội dung mở trong cửa sổ trên bảng; đóng cửa sổ để viết lên trang này.';
  return `Bài nộp gồm ${nouns.map((noun, i) => `${counts[i]} ${noun}`).join(', ')}. ${hint}`;
}

/** The painted page every submission has: the written answer, or a cover naming its attachments. No mark: the board is projected. */
export function answerDocument(submission: StudentSubmission): BoardDocument {
  const meta = [submission.taskTitle, submission.submittedAt ? `Nộp lúc ${vnDateTime(submission.submittedAt)}` : ''].filter(Boolean).join(' • ');
  return {
    id: SUBMISSION_DOCUMENT_PREFIX + submission.id,
    title: `Bài nộp: ${submission.studentName}`,
    kind: 'slide',
    pageCount: 1,
    text: { eyebrow: 'BÀI LÀM CỦA HỌC SINH', title: submission.studentName, meta, html: hasAnswerText(submission.html) ? submission.html : '', empty: attachmentSummary(submissionItems(submission)) },
  };
}

/** An image or PDF answer as a board document, so the pen writes on the student's own page. */
export function fileDocument(submission: StudentSubmission, file: SubmissionFile, index: number): BoardDocument | null {
  if (file.viewer !== 'image' && file.viewer !== 'pdf') return null;
  return { id: `${SUBMISSION_DOCUMENT_PREFIX}${submission.id}-${index}`, title: `Bài nộp: ${submission.studentName}`, kind: 'slide', pageCount: 1, upload: { url: file.url, viewer: file.viewer } };
}

/** An embeddable preview for a Google Drive or Docs share link; null for any other host. */
export function googlePreviewUrl(input: string): string | null {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch (_error) {
    return null;
  }
  const host = url.hostname.toLowerCase();
  const parts = url.pathname.split('/').filter(Boolean);
  if (host === 'drive.google.com') {
    const fileAt = parts.indexOf('d');
    if (parts[0] === 'file' && fileAt === 1 && parts[2]) return `https://drive.google.com/file/d/${encodeURIComponent(parts[2])}/preview`;
    if (parts[0] === 'open' && url.searchParams.get('id')) return `https://drive.google.com/file/d/${encodeURIComponent(url.searchParams.get('id') || '')}/preview`;
    if (parts[0] === 'drive' && parts[1] === 'folders' && parts[2]) return `https://drive.google.com/embeddedfolderview?id=${encodeURIComponent(parts[2])}#grid`;
    return null;
  }
  if (host === 'docs.google.com' && ['document', 'presentation', 'spreadsheets'].indexOf(parts[0]) >= 0 && parts[1] === 'd' && parts[2]) {
    return `https://docs.google.com/${parts[0]}/d/${encodeURIComponent(parts[2])}/preview`;
  }
  return null;
}
